import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type Company,
  type Counterparty,
  type Invoice,
  type InvoiceDraft,
  type InvoiceLine,
  type PriceMode,
  type CorrectionCause,
  type CorrectionRequest,
  type Product,
  type RyczaltRateCode,
  type Settlement,
  type VatRateCode,
  RYCZALT_RATES,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, currencyLabel, date, money } from '../format';
import * as Icon from '../components/Icons';
import { ViesStatus } from '../components/ViesStatus';
import { CounterpartyDialog } from '../components/CounterpartyDialog';
import { InvoiceDetail } from '../components/InvoiceDetail';
import { ProductDialog } from '../components/ProductDialog';
import { RemotePicker } from '../components/RemotePicker';
import { convertUnitPrice, normalizeUnitPrice, previewLines } from '../invoicePreview';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import type { Panel } from '../components/Shell';

const VAT_RATES: VatRateCode[] = ['23', '8', '5', '0', 'zw', 'np', 'oo'];

const today = () => new Date().toISOString().slice(0, 10);

const emptyLine: InvoiceLine = { name: '', unit: 'szt.', quantity: '1', unitPrice: '0.00', vatRate: '23' };

function emptyDraft(): InvoiceDraft {
  return {
    issueDate: today(),
    saleDate: today(),
    dueDate: null,
    counterpartyId: null,
    priceMode: 'NET',
    currency: 'PLN',
    lines: [{ ...emptyLine }],
  };
}

/** The list response omits lines; an opened draft comes from GET /invoices/{id}. */
export function draftFromInvoice(invoice: Invoice): InvoiceDraft {
  return {
    issueDate: invoice.issueDate,
    saleDate: invoice.saleDate,
    dueDate: invoice.dueDate,
    counterpartyId: invoice.counterpartyId,
    priceMode: invoice.priceMode,
    currency: invoice.currency,
    exchangeRate: String(invoice.exchangeRate),
    bankAccountId: invoice.bankAccountId,
    paymentMethod: invoice.paymentMethod,
    notes: invoice.notes,
    lines: invoice.lines.map((line) => ({
      productId: line.productId,
      name: line.name,
      unit: line.unit,
      quantity: String(line.quantity),
      unitPrice: String(line.unitPrice),
      vatRate: line.vatRate,
      gtuCode: line.gtuCode,
      pkwiu: line.pkwiu,
      ryczaltRate: line.ryczaltRate,
    })),
  };
}

type Tab = 'all' | 'drafts' | 'issued' | 'unpaid' | 'corrections';

export function InvoicesPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  /** Stan płatności to co innego niż stan dokumentu: „Wystawiona” i „26 dni po terminie” to dwie różne informacje. */
  const [payments, setPayments] = useState<Map<string, Settlement>>(new Map());
  // Stawkę ryczałtu pokazujemy tylko firmom, które rozliczają się ryczałtem
  const [onRyczalt, setOnRyczalt] = useState(false);
  const [draft, setDraft] = useState<InvoiceDraft | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  // Korekta zaczyna się od stanu bieżącego faktury — po wszystkich wcześniejszych korektach
  const [correction, setCorrection] = useState<{
    invoiceId: string;
    number: string | null;
    priceMode: PriceMode;
    currency: string;
    form: CorrectionRequest;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);
  /** Otwarta faktura: pojedynczy dokument z podglądem, zapłatami i notatką. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [year, setYear] = useState('');

  const load = useCallback(async () => {
    try {
      const [loaded, contacts, catalogue, settings, open] = await Promise.all([
        api.invoices(company.id),
        api.counterparties(company.id, '', false),
        api.products(company.id, '', false),
        api.settings(company.id),
        api.receivables(company.id, false).catch(() => [] as Settlement[]),
      ]);
      setInvoices(loaded);
      setCounterparties((previous) => {
        const loadedIds = new Set(contacts.map((item) => item.id));
        return [...contacts, ...previous.filter((item) => !loadedIds.has(item.id))];
      });
      setProducts(catalogue);
      setOnRyczalt(settings[0]?.booksType === 'RYCZALT');
      setPayments(new Map(open.map((item) => [item.documentId, item])));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = !company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER');
  const counterpartyName = (id: string | null) =>
    counterparties.find((item) => item.id === id)?.name ?? null;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  /** Serwer ostatecznie liczy kwoty przy zapisie; formularz pokazuje bieżący podgląd. */
  async function saveDraft() {
    if (!draft) return;
    await run(async () => {
      const saved = draftId
        ? await api.updateInvoice(company.id, draftId, draft)
        : await api.createInvoice(company.id, draft);
      setDraftId(saved.id);
    });
  }

  async function editDraft(id: string) {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.invoice(company.id, id);
      if (saved.status !== 'DRAFT') throw new Error(t.invoices.draftUnavailable);
      if (saved.counterpartyId && !counterparties.some((item) => item.id === saved.counterpartyId)) {
        const buyer = await api.counterparty(company.id, saved.counterpartyId);
        setCounterparties((previous) => previous.some((item) => item.id === buyer.id)
          ? previous : [...previous, buyer]);
      }
      setDraft(draftFromInvoice(saved));
      setDraftId(saved.id);
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  const years = useMemo(
    () => [...new Set((invoices ?? []).map((invoice) => invoice.issueDate.slice(0, 4)))].sort().reverse(),
    [invoices],
  );

  const counts = useMemo(() => {
    const list = invoices ?? [];
    return {
      all: list.length,
      drafts: list.filter((invoice) => invoice.status === 'DRAFT').length,
      issued: list.filter((invoice) => invoice.status === 'ISSUED').length,
      unpaid: list.filter((invoice) => payments.has(invoice.id)).length,
      corrections: list.filter((invoice) => invoice.documentKind === 'CORRECTION').length,
    };
  }, [invoices, payments]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (invoices ?? []).filter((invoice) => {
      if (year && !invoice.issueDate.startsWith(year)) return false;
      if (tab === 'drafts' && invoice.status !== 'DRAFT') return false;
      if (tab === 'issued' && invoice.status !== 'ISSUED') return false;
      if (tab === 'unpaid' && !payments.has(invoice.id)) return false;
      if (tab === 'corrections' && invoice.documentKind !== 'CORRECTION') return false;
      if (!needle) return true;
      return [invoice.number, counterpartyName(invoice.counterpartyId), invoice.totalGross]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [invoices, tab, query, year, payments, counterparties]);

  const totals = shown.reduce(
    (sum, invoice) => ({
      net: sum.net + Number(invoice.totalNet),
      vat: sum.vat + Number(invoice.totalVat),
      gross: sum.gross + Number(invoice.totalGross),
    }),
    { net: 0, vat: 0, gross: 0 },
  );
  const unit = currencyLabel(shown[0]?.currency ?? 'PLN', locale);

  const opened = (invoices ?? []).find((invoice) => invoice.id === openId) ?? null;
  if (opened) {
    return (
      <InvoiceDetail
        company={company}
        invoice={opened}
        settlement={payments.get(opened.id)}
        editable={editable}
        t={t}
        locale={locale}
        onClose={() => setOpenId(null)}
        onChanged={load}
        onEditDraft={(id) => {
          setOpenId(null);
          void editDraft(id);
        }}
        onCorrect={(invoice) => {
          setOpenId(null);
          void run(async () => {
            const state = await api.invoiceCurrentState(company.id, invoice.id);
            setCorrection({
              invoiceId: invoice.id,
              number: invoice.number,
              priceMode: invoice.priceMode,
              currency: invoice.currency,
              form: { issueDate: today(), cause: 'MISTAKE', reason: '', lines: state.lines },
            });
          });
        }}
      />
    );
  }

  if (draft || correction) {
    return (
      <InvoiceForm
        company={company}
        t={t}
        locale={locale}
        draft={draft}
        draftId={draftId}
        correction={correction}
        counterparties={counterparties}
        onCounterpartyAdded={(created) => setCounterparties((prev) =>
          prev.some((item) => item.id === created.id) ? prev : [...prev, created])}
        products={products}
        onProductAdded={(created) => setProducts((prev) => [...prev, created])}
        onRyczalt={onRyczalt}
        busy={busy}
        error={error?.message ?? null}
        onDraft={setDraft}
        onCorrection={setCorrection}
        onSave={saveDraft}
        onRun={run}
        onClose={() => {
          setDraft(null);
          setDraftId(null);
          setCorrection(null);
        }}
      />
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.invoices.title}</h3>
          <p>
            {[
              year || (years.length === 1 ? years[0] : null),
              `${shown.length} · ${money(totals.gross, locale)} ${t.invoices.gross}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        {editable && (
          <div className="sp">
            <button
              type="button"
              className="btn"
              onClick={() => {
                setDraft(emptyDraft());
                setDraftId(null);
              }}
            >
              <Icon.Plus />
              {t.invoices.new}
            </button>
          </div>
        )}
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      <div className="panel">
        <div className="tabs" role="tablist">
          {(
            [
              ['all', t.invoices.tabAll],
              ['drafts', t.invoices.statuses.DRAFT],
              ['issued', t.invoices.statuses.ISSUED],
              ['unpaid', t.invoices.tabUnpaid],
              ['corrections', t.invoices.tabCorrections],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button type="button" role="tab" key={key} aria-selected={tab === key} onClick={() => setTab(key)}>
              {label} · {counts[key]}
            </button>
          ))}
        </div>

        <div className="row">
          <input
            className="inp"
            style={{ flex: '1 1 220px' }}
            placeholder={t.invoices.filter}
            aria-label={t.invoices.filter}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {years.length > 1 && (
            <select
              className="inp w"
              aria-label={t.dashboard.period}
              value={year}
              onChange={(event) => setYear(event.target.value)}
            >
              <option value="">{t.invoices.allYears}</option>
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          )}
        </div>

        {shown.length === 0 ? (
          <div className="empty">
            <b>{t.invoices.none}</b>
          </div>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.invoices.number}</th>
                  <th>{t.invoices.issueDate}</th>
                  <th>{t.invoices.counterparty}</th>
                  <th>{t.invoices.status}</th>
                  <th>{t.invoices.payment}</th>
                  <th className="n">
                    {t.invoices.netAmount} ({unit})
                  </th>
                  <th className="n">
                    {t.invoices.vat} ({unit})
                  </th>
                  <th className="n">
                    {t.invoices.grossAmount} ({unit})
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((invoice) => {
                  const payment = payments.get(invoice.id);
                  return (
                    <tr key={invoice.id}>
                      <td className="doc">
                        <button type="button" className="btn plain sm" onClick={() => setOpenId(invoice.id)}>
                          {invoice.number ?? t.invoices.statuses[invoice.status]}
                        </button>
                        {invoice.documentKind === 'CORRECTION' && (
                          <span className="muted sm"> · {t.invoices.correction}</span>
                        )}
                      </td>
                      <td className="doc">{date(invoice.issueDate, locale)}</td>
                      <td>{counterpartyName(invoice.counterpartyId) ?? '—'}</td>
                      <td>
                        <span className={invoice.status === 'ISSUED' ? 'chip st-info' : 'chip st-draft'}>
                          <i />
                          {t.invoices.statuses[invoice.status]}
                        </span>
                      </td>
                      <td>
                        {payment ? (
                          <span className={payment.daysOverdue > 0 ? 'chip st-bad' : 'chip st-warn'}>
                            <i />
                            {payment.daysOverdue > 0
                              ? `${t.settlements.overdueDays} ${payment.daysOverdue}`
                              : t.settlements.statuses[payment.status]}
                          </span>
                        ) : invoice.status === 'ISSUED' ? (
                          <span className="chip st-ok">
                            <i />
                            {t.settlements.statuses.PAID}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="n">{amount(invoice.totalNet, locale)}</td>
                      <td className="n">{amount(invoice.totalVat, locale)}</td>
                      <td className="n">{amount(invoice.totalGross, locale)}</td>
                      <td className="act">
                        {/* Jedna czynność w wierszu: wejście w fakturę. Reszta — plik KSeF, korekta,
                            zatwierdzenie, usunięcie szkicu — stoi w środku, bo pięć przycisków
                            w wierszu rozpychało tabelę tak, że kwoty uciekały poza ekran */}
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy}
                          onClick={() => setOpenId(invoice.id)}
                        >
                          {t.invoices.openInvoice}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Suma w stopce jest obowiązkowa: bez niej listy nie da się uzgodnić z deklaracją */}
              <tfoot>
                <tr>
                  <td colSpan={5}>
                    {t.invoices.totals} · {shown.length}
                  </td>
                  <td className="n">{amount(totals.net, locale)}</td>
                  <td className="n">{amount(totals.vat, locale)}</td>
                  <td className="n">{amount(totals.gross, locale)}</td>
                  {/* Także przyklejona, inaczej suma stanęłaby w innej kolumnie niż liczby, które sumuje */}
                  <td className="act" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/*
       * Wysyłki przez bramkę KSeF nie ma w kodzie. Nie rysujemy przycisku „Wyślij”, którego
       * nie da się kliknąć — zamiast tego mówimy wprost, co działa dzisiaj.
       */}
      <div className="panel future">
        <div className="hd2">
          <b>{t.invoices.ksefFutureTitle}</b>
          <span className="soon">{t.invoices.soon}</span>
        </div>
        <p className="sm muted">{t.invoices.ksefFutureText}</p>
      </div>
    </>
  );
}

/** Formularz faktury i korekty — jeden ekran, nie panel doklejony pod listą. */
function InvoiceForm({
  company,
  t,
  locale,
  draft,
  draftId,
  correction,
  counterparties,
  onCounterpartyAdded,
  products,
  onProductAdded,
  onRyczalt,
  busy,
  error,
  onDraft,
  onCorrection,
  onSave,
  onRun,
  onClose,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  draft: InvoiceDraft | null;
  draftId: string | null;
  correction: { invoiceId: string; number: string | null; priceMode: PriceMode; currency: string; form: CorrectionRequest } | null;
  counterparties: Counterparty[];
  onCounterpartyAdded: (created: Counterparty) => void;
  products: Product[];
  onProductAdded: (created: Product) => void;
  onRyczalt: boolean;
  busy: boolean;
  error: string | null;
  onDraft: (draft: InvoiceDraft | null) => void;
  onCorrection: (correction: { invoiceId: string; number: string | null; priceMode: PriceMode; currency: string; form: CorrectionRequest } | null) => void;
  onSave: () => Promise<void>;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
  onClose: () => void;
}) {
  const [addingCounterparty, setAddingCounterparty] = useState(false);
  const [addingProductAt, setAddingProductAt] = useState<number | null>(null);
  const editedLines = draft?.lines ?? correction?.form.lines ?? [];
  const priceMode = draft?.priceMode ?? correction?.priceMode ?? 'NET';

  function selectCreatedProduct(created: Product) {
    onProductAdded(created);
    if (addingProductAt === null) return;
    const index = addingProductAt;
    const old = editedLines[index];
    if (!old) return;
    const updated = editedLines.map((line, position) => position === index ? {
      ...line,
      productId: created.id,
      name: created.names[locale] ?? created.names.pl ?? line.name,
      unit: created.unit,
      vatRate: created.vatRate,
      gtuCode: created.gtuCode,
      pkwiu: created.pkwiu,
      unitPrice: created.defaultPrice != null
        ? convertUnitPrice(String(created.defaultPrice), created.vatRate, 'NET', priceMode)
        : line.unitPrice,
    } : line);
    if (draft) onDraft({ ...draft, lines: updated });
    if (correction) onCorrection({ ...correction, form: { ...correction.form, lines: updated } });
    setAddingProductAt(null);
  }

  return (
    <>
      {addingCounterparty && (
        <CounterpartyDialog
          companyId={company.id}
          t={t}
          onCreated={(created) => {
            onCounterpartyAdded(created);
            // Nowy kontrahent od razu staje się nabywcą — po to się go tu dodaje
            if (draft) onDraft({ ...draft, counterpartyId: created.id });
            setAddingCounterparty(false);
          }}
          onClose={() => setAddingCounterparty(false)}
        />
      )}
      {addingProductAt !== null && editedLines[addingProductAt] && (
        <ProductDialog
          companyId={company.id}
          line={editedLines[addingProductAt]}
          netPrice={convertUnitPrice(editedLines[addingProductAt].unitPrice,
            editedLines[addingProductAt].vatRate, priceMode, 'NET')}
          t={t}
          locale={locale}
          onCreated={selectCreatedProduct}
          onClose={() => setAddingProductAt(null)}
        />
      )}
      <div className="hd">
        <div>
          <h3>{correction ? `${t.invoices.correctionTo} ${correction.number}`
            : draftId ? t.invoices.editDraft : t.invoices.new}</h3>
          <p>{correction ? t.invoices.correctionHint : t.invoices.draftHint}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {correction && (
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void onRun(async () => {
              await api.correctInvoice(company.id, correction.invoiceId, correction.form);
              onCorrection(null);
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="cIssue">{t.invoices.issueDate}</label>
              <input
                id="cIssue"
                className="inp"
                type="date"
                value={correction.form.issueDate}
                onChange={(event) =>
                  onCorrection({ ...correction, form: { ...correction.form, issueDate: event.target.value } })
                }
                required
              />
            </div>
            <div className="field">
              <label htmlFor="cCause">{t.invoices.correctionCause}</label>
              <select
                id="cCause"
                className="inp"
                value={correction.form.cause}
                onChange={(event) =>
                  onCorrection({
                    ...correction,
                    form: { ...correction.form, cause: event.target.value as CorrectionCause },
                  })
                }
              >
                <option value="MISTAKE">{t.invoices.causes.MISTAKE}</option>
                <option value="EVENT">{t.invoices.causes.EVENT}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="cReason">{t.invoices.correctionReason}</label>
              <input
                id="cReason"
                className="inp"
                value={correction.form.reason}
                onChange={(event) =>
                  onCorrection({ ...correction, form: { ...correction.form, reason: event.target.value } })
                }
                required
              />
            </div>
          </div>

          <header>
            <h4>{t.invoices.linesAfter}</h4>
          </header>
          <LineEditor
            companyId={company.id}
            lines={correction.form.lines}
            onChange={(lines) => onCorrection({ ...correction, form: { ...correction.form, lines } })}
            products={products}
            priceMode={correction.priceMode}
            currency={correction.currency}
            onAddProduct={setAddingProductAt}
            onRyczalt={onRyczalt}
            t={t}
            locale={locale}
          />

          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.invoices.saveDraft}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>
      )}

      {draft && (
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void onSave();
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="iIssue">{t.invoices.issueDate}</label>
              <input
                id="iIssue"
                className="inp"
                type="date"
                value={draft.issueDate}
                onChange={(event) => onDraft({ ...draft, issueDate: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="iSale">{t.invoices.saleDate}</label>
              <input
                id="iSale"
                className="inp"
                type="date"
                value={draft.saleDate}
                onChange={(event) => onDraft({ ...draft, saleDate: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="iDue">{t.invoices.dueDate}</label>
              <input
                id="iDue"
                className="inp"
                type="date"
                value={draft.dueDate ?? ''}
                onChange={(event) => onDraft({ ...draft, dueDate: event.target.value || null })}
              />
            </div>
            <div className="field">
              <label htmlFor="iMode">{t.invoices.priceMode}</label>
              <select
                id="iMode"
                className="inp"
                value={draft.priceMode}
                onChange={(event) => {
                  const next = event.target.value as PriceMode;
                  onDraft({ ...draft, priceMode: next, lines: draft.lines.map((line) => ({
                    ...line,
                    unitPrice: convertUnitPrice(line.unitPrice, line.vatRate, draft.priceMode, next),
                  })) });
                }}
              >
                <option value="NET">{t.invoices.priceModes.NET}</option>
                <option value="GROSS">{t.invoices.priceModes.GROSS}</option>
              </select>
            </div>
          </div>
          <div className="field invoice-buyer-field">
            <label htmlFor="iBuyer">{t.invoices.counterparty}</label>
            <div className="invoice-buyer-control">
              <RemotePicker<Counterparty>
                id="iBuyer"
                value={draft.counterpartyId ?? null}
                selectedLabel={counterparties.find((item) => item.id === draft.counterpartyId)?.name ?? null}
                label={t.invoices.counterparty}
                placeholder={t.counterparties.search}
                clearLabel={t.invoices.chooseCounterparty}
                emptyLabel={t.invoices.noMatches}
                errorLabel={t.invoices.searchFailed}
                refineLabel={t.invoices.refineSearch}
                search={(query) => api.counterparties(company.id, query, false)}
                itemLabel={(item) => item.nip ? `${item.name} · ${item.nip}` : item.name}
                onSelect={(item) => {
                  if (item) onCounterpartyAdded(item);
                  onDraft({ ...draft, counterpartyId: item?.id ?? null });
                }}
              />
              <button type="button" className="btn sec" title={t.counterparties.add}
                aria-label={t.counterparties.add} onClick={() => setAddingCounterparty(true)}>
                <Icon.Plus />
              </button>
            </div>
          </div>

          {/*
            Numer VAT-UE stoi przy kontrahencie, bo tu zapada decyzja o stawce: 0% przy dostawie
            do Unii i „np" przy usłudze rozliczanej przez nabywcę zależą od jego ważności.
            Ostrzegamy tylko wtedy, gdy któraś pozycja ma taką stawkę.
          */}
          <ViesStatus
            companyId={company.id}
            counterparty={counterparties.find((item) => item.id === draft.counterpartyId)}
            matters={draft.lines.some((line) => line.vatRate === 'np' || line.vatRate === '0')}
            t={t}
            locale={locale}
          />

          <header>
            <h4>{t.invoices.lines}</h4>
          </header>
          <LineEditor
            companyId={company.id}
            lines={draft.lines}
            onChange={(lines) => onDraft({ ...draft, lines })}
            products={products}
            priceMode={draft.priceMode}
            currency={draft.currency}
            onAddProduct={setAddingProductAt}
            onRyczalt={onRyczalt}
            t={t}
            locale={locale}
          />

          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.invoices.saveDraft}
            </button>
            {draftId && (
              <button
                type="button"
                className="btn sec"
                disabled={busy}
                onClick={() =>
                  void onRun(async () => {
                    await api.issueInvoice(company.id, draftId);
                    onClose();
                  })
                }
              >
                {t.invoices.issue}
              </button>
            )}
            <div className="sp">
              <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );
}

/**
 * Edytor pozycji — ten sam na fakturze i na korekcie. Na korekcie pozycje opisują stan
 * po zmianie, więc wygląda identycznie; różnicę liczy serwer.
 */
export function LineEditor({
  companyId,
  lines,
  onChange,
  products,
  priceMode,
  currency,
  onAddProduct,
  onRyczalt,
  t,
  locale,
}: {
  companyId: string;
  lines: InvoiceLine[];
  onChange: (lines: InvoiceLine[]) => void;
  products: Product[];
  priceMode: PriceMode;
  currency: string;
  onAddProduct: (index: number) => void;
  onRyczalt: boolean;
  t: Dictionary;
  locale: Locale;
}) {
  const [focusedPrice, setFocusedPrice] = useState<number | null>(null);
  const previews = previewLines(lines, priceMode);
  const totals = previews.reduce((sum, line) => ({
    net: sum.net + line.net,
    vat: sum.vat + line.vat,
    gross: sum.gross + line.gross,
  }), { net: 0, vat: 0, gross: 0 });
  const vatGroups = VAT_RATES.flatMap((rate) => {
    const indexes = lines.flatMap((line, index) => line.vatRate === rate ? [index] : []);
    return indexes.length ? [{ rate, vat: indexes.reduce((sum, index) => sum + previews[index].vat, 0) }] : [];
  });
  const columnCount = onRyczalt ? 10 : 9;
  const update = (index: number, change: Partial<InvoiceLine>) =>
    onChange(lines.map((line, position) => (position === index ? { ...line, ...change } : line)));

  function fillFromProduct(index: number, product: Product | null) {
    if (!product) {
      update(index, { productId: null });
      return;
    }
    update(index, {
      productId: product.id,
      name: product.names[locale] ?? product.names.pl ?? Object.values(product.names)[0] ?? '',
      unit: product.unit,
      unitPrice: product.defaultPrice != null
        ? convertUnitPrice(String(product.defaultPrice), product.vatRate, 'NET', priceMode)
        : lines[index].unitPrice,
      vatRate: product.vatRate,
      gtuCode: product.gtuCode,
      pkwiu: product.pkwiu,
    });
  }

  return (
    <>
      <div className={`tbl invoice-lines${onRyczalt ? ' with-ryczalt' : ''}`}>
        <table>
          <colgroup>
            <col className="invoice-catalog" />
            <col className="invoice-name" />
            <col className="invoice-quantity" />
            <col className="invoice-unit" />
            <col className="invoice-price" />
            <col className="invoice-vat-rate" />
            <col className="invoice-vat-amount" />
            <col className="invoice-price" />
            {onRyczalt && <col className="invoice-ryczalt" />}
            <col className="invoice-action" />
          </colgroup>
          <thead>
            <tr>
              <th>{t.invoices.fromCatalog}</th>
              <th>{t.invoices.lineName}</th>
              <th className="n">{t.invoices.quantity}</th>
              <th>{t.invoices.unit}</th>
              <th className="n">{t.invoices.netUnitPrice}</th>
              <th>{t.invoices.vatRate}</th>
              <th className="n">{t.invoices.vatAmount}</th>
              <th className="n">{t.invoices.grossUnitPrice}</th>
              {onRyczalt && <th>{t.invoices.ryczaltRate}</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const selectedProduct = products.find((item) => item.id === line.productId);
              const netPrice = priceMode === 'NET' ? line.unitPrice
                : convertUnitPrice(line.unitPrice, line.vatRate, 'GROSS', 'NET');
              const grossPrice = priceMode === 'GROSS' ? line.unitPrice
                : convertUnitPrice(line.unitPrice, line.vatRate, 'NET', 'GROSS');
              const priceInput = (
                <input
                  className="inp num sm invoice-money-input"
                  value={focusedPrice === index ? line.unitPrice : amount(line.unitPrice, locale)}
                  onFocus={(event) => { setFocusedPrice(index); event.currentTarget.select(); }}
                  onBlur={() => {
                    setFocusedPrice(null);
                    update(index, { unitPrice: normalizeUnitPrice(line.unitPrice) });
                  }}
                  onChange={(event) => update(index, {
                    unitPrice: event.target.value.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.'),
                  })}
                  inputMode="decimal"
                  aria-label={priceMode === 'NET' ? t.invoices.netUnitPrice : t.invoices.grossUnitPrice}
                  required
                />
              );
              return (
                <tr key={index}>
                  <td>
                    <div className="invoice-catalog-control">
                      <RemotePicker<Product>
                        value={line.productId ?? null}
                        selectedLabel={line.productId
                          ? (selectedProduct?.names[locale] ?? selectedProduct?.names.pl ?? line.name)
                          : null}
                        label={t.invoices.fromCatalog}
                        placeholder={t.invoices.fromCatalog}
                        clearLabel={t.invoices.noCatalog}
                        emptyLabel={t.invoices.noMatches}
                        errorLabel={t.invoices.searchFailed}
                        refineLabel={t.invoices.refineSearch}
                        search={(query) => api.products(companyId, query, false)}
                        itemLabel={(product) => product.code
                          ? `${product.names[locale] ?? product.names.pl ?? product.code} · ${product.code}`
                          : product.names[locale] ?? product.names.pl ?? ''}
                        onSelect={(product) => fillFromProduct(index, product)}
                        compact
                      />
                      <button type="button" className="btn sec sm invoice-add-product"
                        title={t.invoices.addToCatalog} aria-label={t.invoices.addToCatalog}
                        onClick={() => onAddProduct(index)}><Icon.Plus /></button>
                    </div>
                  </td>
                  <td><input className="inp sm" value={line.name}
                    onChange={(event) => update(index, { name: event.target.value })}
                    aria-label={t.invoices.lineName} required /></td>
                  <td className="n"><input className="inp num sm" value={line.quantity}
                    onChange={(event) => update(index, { quantity: event.target.value.replace(',', '.') })}
                    inputMode="decimal" aria-label={t.invoices.quantity} required /></td>
                  <td><input className="inp sm" value={line.unit}
                    onChange={(event) => update(index, { unit: event.target.value })}
                    aria-label={t.invoices.unit} /></td>
                  <td className="n">{priceMode === 'NET' ? priceInput
                    : <output aria-label={t.invoices.netUnitPrice}>{amount(netPrice, locale)}</output>}</td>
                  <td><select className="inp sm" value={line.vatRate}
                    onChange={(event) => update(index, { vatRate: event.target.value as VatRateCode })}
                    aria-label={t.invoices.vatRate} title={t.products.rates[line.vatRate]}>
                    {VAT_RATES.map((rate) => <option key={rate} value={rate}>{t.products.rates[rate]}</option>)}
                  </select></td>
                  <td className="n"><output aria-label={t.invoices.vatAmount}>
                    {amount(previews[index].vat, locale)}
                  </output></td>
                  <td className="n">{priceMode === 'GROSS' ? priceInput
                    : <output aria-label={t.invoices.grossUnitPrice}>{amount(grossPrice, locale)}</output>}</td>
                  {onRyczalt && <td><select className="inp sm" value={line.ryczaltRate ?? ''}
                    onChange={(event) => update(index, {
                      ryczaltRate: (event.target.value || null) as RyczaltRateCode | null,
                    })} aria-label={t.invoices.ryczaltRate} required>
                    <option value="">—</option>
                    {RYCZALT_RATES.map((rate) => <option key={rate} value={rate}>{rate}%</option>)}
                  </select></td>}
                  <td className="act">{lines.length > 1 &&
                    <button type="button" className="btn danger sm"
                      onClick={() => onChange(lines.filter((_, position) => position !== index))}>
                      {t.invoices.removeLine}
                    </button>}</td>
                </tr>
              );
            })}
            <tr className="invoice-add-row">
              <td colSpan={columnCount}>
                <button type="button" className="btn ghost sm"
                  onClick={() => onChange([...lines, { ...emptyLine }])}>
                  <Icon.Plus />{t.invoices.addLine}
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot className="invoice-summary">
            <tr>
              <td colSpan={4} className="invoice-summary-label">{t.invoices.netAmount}</td>
              <td className="n"><b>{money(totals.net, locale, currency)}</b></td>
              <td /><td /><td />
              <td colSpan={onRyczalt ? 2 : 1} />
            </tr>
            {vatGroups.map(({ rate, vat }) => (
              <tr key={rate}>
                <td colSpan={4} className="invoice-summary-label">
                  {t.invoices.vat} {/^\d+$/.test(rate) ? t.products.rates[rate] : `— ${t.products.rates[rate]}`}
                </td>
                <td /><td />
                <td className="n"><b>{money(vat, locale, currency)}</b></td>
                <td />
                <td colSpan={onRyczalt ? 2 : 1} />
              </tr>
            ))}
            {vatGroups.length > 1 && (
              <tr>
                <td colSpan={4} className="invoice-summary-label">{t.invoices.vatTotal}</td>
                <td /><td />
                <td className="n"><b>{money(totals.vat, locale, currency)}</b></td>
                <td />
                <td colSpan={onRyczalt ? 2 : 1} />
              </tr>
            )}
            <tr className="invoice-summary-total">
              <td colSpan={4} className="invoice-summary-label">{t.invoices.grossAmount}</td>
              <td /><td /><td />
              <td className="n"><b>{money(totals.gross, locale, currency)}</b></td>
              <td colSpan={onRyczalt ? 2 : 1} />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
